export interface OrderItemOptionsInput {
  selected_variant?: string | null;
  selectedVariant?: string | null;
  selected_add_ons?: unknown;
  selectedAddOns?: unknown;
  selected_configurations?: unknown;
  selectedConfigurations?: unknown;
  customization_display?: string | null;
  customizationDisplay?: string | null;
}

export function parseOrderItemOptionMaps(
  raw: unknown
): Record<string, unknown>[] {
  if (raw == null) return [];
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed || trimmed === '[]' || trimmed === '{}') return [];
    try {
      return parseOrderItemOptionMaps(JSON.parse(trimmed));
    } catch {
      return [];
    }
  }
  if (Array.isArray(raw)) {
    return raw.flatMap(entry => {
      if (entry == null) return [];
      if (typeof entry === 'string') return parseOrderItemOptionMaps(entry);
      if (typeof entry === 'object' && !Array.isArray(entry)) {
        return [entry as Record<string, unknown>];
      }
      return [];
    });
  }
  if (typeof raw === 'object') {
    return [raw as Record<string, unknown>];
  }
  return [];
}

function scalarConfigValue(
  config: Record<string, unknown>,
  configName: string
): string {
  const keys = [
    'selected_option',
    'selectedOption',
    'value',
    'option',
    'selected_value',
    'selectedValue',
    'option_label',
    'optionLabel',
  ];
  for (const key of keys) {
    const value = config[key];
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  const label = config.label;
  if (label != null) {
    const labelStr = String(label).trim();
    if (labelStr && labelStr.toLowerCase() !== configName.toLowerCase()) {
      return labelStr;
    }
  }
  return '';
}

function expandConfigurationEntries(
  configs: Record<string, unknown>[]
): Record<string, unknown>[] {
  const expanded: Record<string, unknown>[] = [];

  for (const config of configs) {
    const configName = String(config.name ?? '').trim();
    const plural = config.selected_options ?? config.selectedOptions;
    if (Array.isArray(plural) && plural.length > 0) {
      for (const entry of plural) {
        let value = '';
        if (typeof entry === 'string' || typeof entry === 'number') {
          value = String(entry).trim();
        } else if (
          entry &&
          typeof entry === 'object' &&
          !Array.isArray(entry)
        ) {
          value = scalarConfigValue(
            entry as Record<string, unknown>,
            configName
          );
        }
        expanded.push({
          ...config,
          selected_option: value,
        });
      }
      continue;
    }

    const nestedOptions = config.options;
    if (Array.isArray(nestedOptions) && nestedOptions.length > 0) {
      const selectedNested = nestedOptions.filter(
        (opt): opt is Record<string, unknown> =>
          opt != null &&
          typeof opt === 'object' &&
          !Array.isArray(opt) &&
          (opt.selected === true || opt.is_selected === true)
      );
      if (selectedNested.length > 0) {
        for (const opt of selectedNested) {
          expanded.push({
            ...config,
            selected_option: scalarConfigValue(opt, configName),
          });
        }
        continue;
      }
    }

    expanded.push(config);
  }

  return expanded;
}

function extractConfigValue(config: Record<string, unknown>): string {
  const configName = String(config.name ?? '').trim();
  return scalarConfigValue(config, configName);
}

export function formatConfigurationLines(
  configs: Record<string, unknown>[]
): string[] {
  const grouped: Record<string, { name: string; values: string[] }> = {};

  for (const config of expandConfigurationEntries(configs)) {
    const id = config.id != null ? String(config.id) : '';
    const name = String(config.name ?? '').trim();
    const key = id ? `id:${id}` : `name:${name}`;
    const value = extractConfigValue(config);
    const existing = grouped[key] || { name, values: [] };
    existing.values.push(value);
    grouped[key] = existing;
  }

  return Object.keys(grouped)
    .map(key => grouped[key])
    .map(({ name, values }) => {
      const picked = values.filter(value => value);
      if (!name) return picked.join(', ');
      if (picked.length === 0) return null;
      return `${name}: ${picked.join(', ')}`;
    })
    .filter((line): line is string => Boolean(line && line.trim()));
}

function parseAddOnLabels(raw: unknown): string[] {
  return parseOrderItemOptionMaps(raw)
    .map(entry => String(entry.name ?? entry.label ?? '').trim())
    .filter(Boolean);
}

export function formatOrderItemCustomizationDisplay(
  item: OrderItemOptionsInput = {}
): string | null {
  const preset = (
    item.customization_display ?? item.customizationDisplay
  )?.trim();
  if (preset) return preset;

  const parts: string[] = [];
  const configs = parseOrderItemOptionMaps(
    item.selected_configurations ?? item.selectedConfigurations
  );
  const configLines = formatConfigurationLines(configs);
  parts.push(...configLines);

  const variantRaw = item.selected_variant ?? item.selectedVariant;
  const variant = variantRaw != null ? String(variantRaw).trim() : '';
  if (variant) {
    const configValues = new Set(
      expandConfigurationEntries(configs)
        .map(config => extractConfigValue(config).toLowerCase())
        .filter(Boolean)
    );
    if (configValues.size === 0 || !configValues.has(variant.toLowerCase())) {
      parts.unshift(variant);
    }
  }

  parts.push(...parseAddOnLabels(item.selected_add_ons ?? item.selectedAddOns));

  const text = parts.filter(Boolean).join(', ');
  return text || null;
}

export function formatOrderItemSubtitle(
  item: OrderItemOptionsInput & { special_instructions?: string | null } = {}
): string | null {
  const parts: string[] = [];
  const options = formatOrderItemCustomizationDisplay(item);
  if (options) parts.push(options);

  const note = item.special_instructions?.trim();
  if (note) parts.push(note);

  return parts.length > 0 ? parts.join(' · ') : null;
}
